import React, { useState } from 'react';
import { 
  ArrowLeft, 
  Server, 
  Database, 
  Shield, 
  Network, 
  Layers, 
  Settings, 
  Activity, 
  FileText,
  MessageSquare,
  RefreshCw,
  Bell,
  Clock,
  Users,
  Code,
  GitBranch
  ,Lock  
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const ArchitecturePage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');

  const Section = ({ title, children }) => (
    <div className="bg-white/5 rounded-xl border border-white/10 p-6 mb-6">
      <h3 className="text-xl font-bold mb-4 text-white">{title}</h3>
      {children}
    </div>
  );

  const FeatureCard = ({ icon: Icon, title, children }) => (
    <div className="bg-white/5 rounded-xl border border-white/10 p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 rounded-lg bg-[#017D7E]/10">
          <Icon className="w-6 h-6 text-[#017D7E]" />
        </div>
        <h4 className="text-lg font-bold text-white">{title}</h4>
      </div>
      {children}
    </div>
  );

  // Composant pour les diagrammes de flux
  const FlowDiagram = ({ steps, title }) => (
    <div className="bg-white/5 rounded-xl p-4">
      <h4 className="text-lg font-bold mb-4 text-white">{title}</h4>
      <div className="flex flex-col gap-4">
        {steps.map((step, index) => (
          <div key={index} className="flex items-start gap-4">
            <div className="w-8 h-8 rounded-full bg-[#017D7E]/20 flex items-center justify-center text-[#017D7E] font-bold">
              {index + 1}
            </div>
            <div className="flex-1">
              <p className="text-white/70">{step}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderOverviewTab = () => (
    <div>
      <Section title="Vue d'ensemble">
        <p className="text-white/70 mb-6">
          MXPAY est une plateforme complète de gestion des flux entre les systèmes bancaires et SYSTAC2, 
          offrant une solution intégrée pour la transformation et le traitement des paiements.
        </p>

        <div className="grid md:grid-cols-2 gap-6">
          <FeatureCard icon={Settings} title="Objectifs principaux">
            <ul className="space-y-2 text-white/70">
       
              <li>• Support multi-chanels et multi-formats</li>
              <li>• Traçabilité complète des opérations</li>
              <li>• Performance et haute disponibilité</li>
            </ul>
          </FeatureCard>

          <FeatureCard icon={Shield} title="Contraintes & Exigences">
            <ul className="space-y-2 text-white/70">
              <li>• Haute disponibilité 24/7</li>
              <li>• Sécurisation des échanges</li>
              <li>• Performances optimales</li>
              <li>• Conformité réglementaire</li>
              <li>• Évolutivité du système</li>
            </ul>
          </FeatureCard>
        </div>
      </Section>

      <Section title="Architecture Globale">
        <div className="grid md:grid-cols-3 gap-6">
          <FeatureCard icon={Layers} title="Frontend">
            <ul className="space-y-2 text-white/70">
              <li>• Interface React.js moderne</li>
              <li>• Dashboard temps réel</li>
              <li>• Administration système</li>
              <li>• Gestion des utilisateurs</li>
            </ul>
          </FeatureCard>

          <FeatureCard icon={Server} title="Backend">
            <ul className="space-y-2 text-white/70">
              <li>• Spring Boot 3.x</li>
              <li>• Noyau de traitement</li>
              <li>• Gestion ActiveMQ</li>
              <li>• Services distribués</li>
            </ul>
          </FeatureCard>

          <FeatureCard icon={Database} title="Infrastructure">
            <ul className="space-y-2 text-white/70">
              <li>• PostgreSQL 15+</li>
              <li>• ActiveMQ Artemis</li>
              <li>• Clustering</li>
              <li>• Monitoring</li>
            </ul>
          </FeatureCard>
        </div>
      </Section>
    </div>
  );

  const renderComponentsTab = () => (
    <div>
      <Section title="Frontend (React.js)">
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <FeatureCard icon={Users} title="Module Administration">
            <ul className="space-y-2 text-white/70">
              <li>• Gestion des utilisateurs</li>
              <li>• Configuration système</li>
              <li>• Gestion des certificats</li>
              <li>• Paramétrage des conversions</li>
            </ul>
          </FeatureCard>

          <FeatureCard icon={Activity} title="Module Monitoring">
            <ul className="space-y-2 text-white/70">
              <li>• Dashboard temps réel</li>
              <li>• Suivi des flux</li>
              <li>• Alertes et notifications</li>
              <li>• Statistiques d'utilisation</li>
            </ul>
          </FeatureCard>
        </div>
      </Section>

      <Section title="Backend (Spring Boot)">
        <div className="grid md:grid-cols-2 gap-6">
          <FeatureCard icon={Server} title="Services Core">
            <ul className="space-y-2 text-white/70">
              <li>• Traitement des messages</li>
              <li>• Conversion des formats</li>
              <li>• Gestion des queues</li>
              <li>• Validation des données</li>
            </ul>
          </FeatureCard>

          <FeatureCard icon={Database} title="Persistence">
            <ul className="space-y-2 text-white/70">
              <li>• PostgreSQL 15+</li>
              <li>• Schémas optimisés</li>
              <li>• Gestion transactionnelle</li>
              <li>• Backup automatisé</li>
            </ul>
          </FeatureCard>
        </div>
      </Section>
    </div>
  );

  

  const renderSecurityTab = () => (
    <div>
      <Section title="Sécurité et Authentification">
        <div className="grid md:grid-cols-2 gap-6">
          <FeatureCard icon={Shield} title="Contrôle d'accès">
            <ul className="space-y-2 text-white/70">
              <li>• Authentification JWT</li>
              <li>• RBAC avancé</li>
              <li>• SSO intégré</li>
              <li>• Audit des accès</li>
            </ul>
          </FeatureCard>

          <FeatureCard icon={Lock} title="Sécurité des données">
            <ul className="space-y-2 text-white/70">
              <li>• Chiffrement bout en bout</li>
              <li>• Gestion des certificats</li>
              <li>• Validation des signatures</li>
              <li>• Protection des données</li>
            </ul>
          </FeatureCard>
        </div>
      </Section>

      <Section title="Audit et Traçabilité">
        <div className="grid md:grid-cols-2 gap-6">
          <FeatureCard icon={FileText} title="Journalisation">
            <ul className="space-y-2 text-white/70">
              <li>• Logs applicatifs</li>
              <li>• Logs de sécurité</li>
              <li>• Logs de performance</li>
              <li>• Rotation automatique</li>
            </ul>
          </FeatureCard>

          <FeatureCard icon={Activity} title="Monitoring">
            <ul className="space-y-2 text-white/70">
              <li>• Surveillance temps réel</li>
              <li>• Alertes automatiques</li>
              <li>• Tableaux de bord</li>
              <li>• Rapports périodiques</li>
            </ul>
          </FeatureCard>
        </div>
      </Section>
    </div>
  );

  const renderDeploymentTab = () => (
    <div>
      <Section title="Environnements">
        <div className="grid md:grid-cols-2 gap-6">
          <FeatureCard icon={Settings} title="Production">
            <ul className="space-y-2 text-white/70">
              <li>• Haute disponibilité</li>
              <li>• Load balancing</li>
              <li>• Monitoring 24/7</li>
              <li>• Backup automatisé</li>
            </ul>
          </FeatureCard>

          <FeatureCard icon={Server} title="Configuration">
            <ul className="space-y-2 text-white/70">
              <li>• Configuration externalisée</li>
              <li>• Gestion multi-environnements</li>
              <li>• Paramètres système</li>
              <li>• Variables d'environnement</li>
            </ul>
          </FeatureCard>
        </div>

        <div className="mt-6 grid md:grid-cols-2 gap-6">
          <FeatureCard icon={GitBranch} title="Infrastructure Minimale">
            <ul className="space-y-2 text-white/70">
              <li>• 2 serveurs d'application</li>
              <li>• Cluster base de données</li>
              <li>• Serveur monitoring</li>
              <li>• Load balancer</li>
            </ul>
          </FeatureCard>

          <FeatureCard icon={Network} title="Infrastructure Recommandée">
            <ul className="space-y-2 text-white/70">
              <li>• 4+ serveurs d'application</li>
              <li>• Cluster PostgreSQL répliqué</li>
              <li>• Monitoring redondant</li>
              <li>• Load balancers HA</li>
            </ul>
          </FeatureCard>
        </div>
      </Section>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#050A1C] p-8">
      {/* Navigation */}
      <button
        onClick={() => navigate(-1)}
        className="mb-8 flex items-center gap-2 text-white/70 hover:text-white group"
      >
        <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
        Retour
      </button>

      {/* En-tête */}
      <div className="max-w-6xl mx-auto mb-12">
        <h1 className="text-3xl font-bold text-white mb-4">Architecture Technique MXPAY</h1>
        <p className="text-white/70">
          Documentation détaillée de l'architecture et des composants techniques de la solution MXPAY.
        </p>
      </div>

      {/* Navigation des onglets */}
      <div className="max-w-6xl mx-auto mb-8 border-b border-white/10">
        <div className="flex space-x-8">
          {[
            { id: 'overview', label: 'Vue d\'ensemble', icon: Layers },
            { id: 'components', label: 'Composants', icon: Server },
            { id: 'security', label: 'Sécurité', icon: Shield },
            { id: 'deployment', label: 'Déploiement', icon: Network },
            { id: 'monitoring', label: 'Monitoring', icon: Bell }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 pb-4 relative ${
                activeTab === tab.id ? 'text-[#017D7E]' : 'text-white/50 hover:text-white'
              }`}
            >
              <tab.icon className="w-5 h-5" />
              <span>{tab.label}</span>
              {activeTab === tab.id && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#017D7E]" />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Contenu principal */}
      <div className="max-w-6xl mx-auto">
        {activeTab === 'overview' && renderOverviewTab()}
        {activeTab === 'components' && renderComponentsTab()}
     
        {activeTab === 'security' && renderSecurityTab()}
        {activeTab === 'deployment' && renderDeploymentTab()}
        {activeTab === 'monitoring' && (
          <div>
            <Section title="Surveillance Système">
              <div className="grid md:grid-cols-2 gap-6">
                <FeatureCard icon={Bell} title="Alerting">
                  <ul className="space-y-2 text-white/70">
                    <li>• Seuils configurables</li>
                    <li>• Notifications multi-canaux</li>
                    <li>• Escalade automatique</li>
                    <li>• Rapports périodiques</li>
                  </ul>
                </FeatureCard>

                <FeatureCard icon={Activity} title="Métriques système">
                  <ul className="space-y-2 text-white/70">
                    <li>• CPU/Mémoire/Disque</li>
                    <li>• Performance réseau</li>
                    <li>• Temps de réponse</li>
                    <li>• Files d'attente</li>
                  </ul>
                </FeatureCard>
              </div>

              <div className="mt-6 grid md:grid-cols-2 gap-6">
                <FeatureCard icon={Clock} title="Métriques métier">
                  <ul className="space-y-2 text-white/70">
                    <li>• Volume des transactions</li>
                    <li>• Taux de conversion</li>
                    <li>• Taux d'erreur</li>
                    <li>• Temps de traitement</li>
                  </ul>
                </FeatureCard>

                <FeatureCard icon={Code} title="Logging">
                  <ul className="space-y-2 text-white/70">
                    <li>• Centralisation des logs</li>
                    <li>• Rotation automatique</li>
                    <li>• Niveaux configurables</li>
                    <li>• Rétention paramétrable</li>
                  </ul>
                </FeatureCard>
              </div>
            </Section>

            <Section title="Supervision Opérationnelle">
              <div className="grid md:grid-cols-2 gap-6">
                <FeatureCard icon={Settings} title="Outils de supervision">
                  <ul className="space-y-2 text-white/70">
                    <li>• Dashboard temps réel</li>
                    <li>• Console d'administration</li>
                    <li>• Outils de diagnostic</li>
                    <li>• Rapports automatisés</li>
                  </ul>
                </FeatureCard>

                <FeatureCard icon={Shield} title="Monitoring sécurité">
                  <ul className="space-y-2 text-white/70">
                    <li>• Détection des intrusions</li>
                    <li>• Audit des accès</li>
                    <li>• Surveillance certificats</li>
                    <li>• Alertes de sécurité</li>
                  </ul>
                </FeatureCard>
              </div>
            </Section>
          </div>
        )}
      </div>
    </div>
  );
};

export default ArchitecturePage;